import React from 'react';
import PropTypes from 'prop-types';
import {gql} from 'apollo-boost';
import {Query} from 'react-apollo';
import {filterHeadline, filterHTML} from "../../helpers/filters";
import {fieldObjectSectionStyleCss} from "../../helpers/createStyles";
import {fieldObjectSectionStyle} from "../../helpers/graphQLShortcuts";
import Paragraph from "../Paragraph";
import './IconicList.scss';

interface Data {
    paragraphById: {
        fieldIconicListSectionHeader: Array<{
            entity: Array<{
                fieldTxtHeadline: string;
                fieldHtmlBody: Array<{
                    value: string;
                    format: string;
                    processed: string;
                }>;
                fieldVarTextAlignment: string;
            }>
        }>;
        fieldIconicListSectionFooter: Array<{
            entity: Array<{
                fieldTxtHeadline: string;
                fieldHtmlBody: Array<{
                    value: string;
                    format: string;
                    processed: string;
                }>;
                fieldObjectCta: Array<{targetRevisionId: string}>;
            }>
        }>;
        fieldIconicListSectionStyle: Array<{}>;
        fieldInnerIconicListItems: Array<{}>;
    }
}

class IconicListQuery extends Query<Data, {id: string}> {}

const GET_CARDS = gql`
query paragraphById($id: String!) {
  paragraphById:paragraphRevisionById(id: $id) {
    ... on Paragraph {
      ... on Paragraph {
        ... on ParagraphComponentIconicList {
          fieldIconicListSectionHeader {
            ... on FieldParagraphFieldIconicListSectionHeader {
              entity {
                ... on Paragraph {
                  ... on ParagraphInnerSectionHeader {
                    fieldTxtHeadline
                    fieldHtmlBody {
                      value
                      format
                      processed
                    }
                    fieldVarTextAlignment
                  }
                }
              }
            }
          }
          fieldIconicListSectionFooter {
            ... on FieldParagraphFieldIconicListSectionFooter {
              entity {
                ... on Paragraph {
                  ... on ParagraphInnerSectionFooter {
                    fieldTxtHeadline
                    fieldHtmlBody {
                      value
                      format
                      processed
                    }
                    fieldObjectCta {
                      targetRevisionId
                    }
                  }
                }
              }
            }
          }
          fieldIconicListSectionStyle {
            ... on FieldParagraphFieldIconicListSectionStyle {
                ${fieldObjectSectionStyle}
            }
          }
          fieldInnerIconicListItems {
            ... on FieldParagraphFieldInnerIconicListItems {
              entity {
                ... on ParagraphInnerIconicListItem {
                  fieldTxtHeadline
                  fieldHtmlBody {
                    processed
                  }
                  fieldIconFile {
                    ... on FieldParagraphFieldIconFile {
                      entity {
                        ... on File {
                          url
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
`;

const IconicList = ({id, markComponentLoadedAs, handleLoadedComponents}) => (
    <IconicListQuery asyncMode query={GET_CARDS} variables={{ id }}>
        {({ loading, error, data }) => {
            /* eslint-disable react/no-danger */
            if (loading) return null;
            if (error) {
                markComponentLoadedAs(id, true);
                return <div>{error}</div>;
            }

            const field = data.paragraphById;

            const header = field.fieldIconicListSectionHeader ? field.fieldIconicListSectionHeader.entity : null;
            const footer = field.fieldIconicListSectionFooter ? field.fieldIconicListSectionFooter.entity : null;
            const innerList = field.fieldInnerIconicListItems && field.fieldInnerIconicListItems.length ? field.fieldInnerIconicListItems : null;
            const styles = fieldObjectSectionStyleCss(field.fieldIconicListSectionStyle);

            markComponentLoadedAs(id, true);

            return (
                <div className={`iconicList${styles.classNames}`} style={styles.css} id={styles.id}>
                    {styles.childBackground.isActive && (
                        <div style={styles.childBackground.css}/>
                    )}
                    <div className="container">

                        {header && ((header.fieldTxtHeadline) || (header.fieldHtmlBody && header.fieldHtmlBody.processed)) && (
                            <div className="iconicList__header">
                                {header.fieldTxtHeadline && (
                                    // eslint-disable-next-line react/no-danger
                                    <h2 style={{textAlign: header.fieldVarTextAlignment}}
                                        dangerouslySetInnerHTML={{__html: filterHeadline(header.fieldTxtHeadline)}} />
                                )}

                                {header.fieldHtmlBody && header.fieldHtmlBody.processed && (
                                    // eslint-disable-next-line react/no-danger
                                    <div style={{textAlign: header.fieldVarTextAlignment}}
                                         dangerouslySetInnerHTML={{__html: filterHTML(header.fieldHtmlBody.processed)}}/>
                                )}
                            </div>
                        )}

                        {innerList.length ? (
                            <div className="iconicList__items">
                                {innerList.map(item => (
                                    <div className="iconicListItem">
                                        <div className="iconicListItem__inner">
                                            {item.entity.fieldIconFile && item.entity.fieldIconFile.entity && item.entity.fieldIconFile.entity.url && (
                                                <div className="iconicListItem__image">
                                                    <img src={item.entity.fieldIconFile.entity.url} alt=""/>
                                                </div>
                                            )}
                                            <div className="iconicListItem__content">
                                                {item.entity.fieldTxtHeadline && (
                                                    // eslint-disable-next-line react/no-danger
                                                    <h3 dangerouslySetInnerHTML={{__html: filterHeadline(item.entity.fieldTxtHeadline)}} />
                                                )}

                                                {item.entity.fieldHtmlBody && item.entity.fieldHtmlBody.processed && (
                                                    // eslint-disable-next-line react/no-danger
                                                    <div dangerouslySetInnerHTML={{__html: filterHTML(item.entity.fieldHtmlBody.processed)}}/>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        {footer && ((footer.fieldTxtHeadline) || (footer.fieldObjectCta && footer.fieldObjectCta.length) || (footer.fieldHtmlBody && footer.fieldHtmlBody.processed)) && (
                            <div className="iconicList__footer">
                                {footer.fieldTxtHeadline && (
                                    // eslint-disable-next-line react/no-danger
                                    <h4 dangerouslySetInnerHTML={{__html: filterHeadline(footer.fieldTxtHeadline)}} />
                                )}
                                {footer.fieldHtmlBody && footer.fieldHtmlBody.processed && (
                                    // eslint-disable-next-line react/no-danger
                                    <div dangerouslySetInnerHTML={{__html: filterHTML(footer.fieldHtmlBody.processed)}}/>
                                )}
                                {footer.fieldObjectCta && footer.fieldObjectCta.map(component => (
                                    <Paragraph key={`${id}_${component.targetRevisionId}`}
                                               id={String(component.targetRevisionId)}
                                               handleLoadedComponents={handleLoadedComponents}/>
                                ))}
                            </div>
                        )}

                    </div>
                </div>
            );
        }}
    </IconicListQuery>
);

IconicList.propTypes = {
    id: PropTypes.number.isRequired,
    markComponentLoadedAs: PropTypes.func.isRequired,
    handleLoadedComponents: PropTypes.func.isRequired
};

export default IconicList;



// WEBPACK FOOTER //
// ./frontend/components/IconicList/IconicList.jsx